import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { ChannelState } from '@x/ecommerce-shop/app/channel/state';
import { AddressPipe } from '@x/ecommerce-shop/app/core/address/pipes/address.pipe';
import { AddressService } from '@x/ecommerce-shop/app/core/address/services/address.service';
import { AuthState } from '@x/ecommerce-shop/app/core/auth/state/auth.state';
import { RegionOverlayService } from '@x/ecommerce-shop/app/core/region/services/region-overlay.service';
import { RegionSetFromAddressInput } from '@x/ecommerce-shop/app/core/region/state/region.actions';
import {
  IRegionStateModel,
  RegionState,
} from '@x/ecommerce-shop/app/core/region/state/region.state';
import { IExpandedAddress } from '@x/geocode/client';
import { filter, map, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, combineLatest, Subscription } from 'rxjs';
import { IShopAddressInput, IShopUserAddress } from '@x/ecommerce/shop-client';

@Component({
  selector: 'x-region-search',
  templateUrl: './region-search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'x-region-search' },
})
export class RegionSearchComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();

  @Select(RegionState) regionState$: Observable<IRegionStateModel>;
  @Select(ChannelState.activeChannelDefaultCountry) defaultCountryCode$: Observable<
    string | null | undefined
  >;

  hideUserAddresses$ = new BehaviorSubject(false);

  userAddresses$: Observable<IShopUserAddress[]> = combineLatest([
    this.store.select(AuthState.userAddresses),
    this.store.select(ChannelState.activeChannel),
    this.hideUserAddresses$,
    this.regionService.showMap$,
  ]).pipe(
    map(([addresses, channelState, hide, showMap]) => {
      const sorted = addresses
        .filter((a) => a.country === channelState?.defaultCountry)
        .sort((a, b) => Number(b.isDefaultShippingAddress) - Number(a.isDefaultShippingAddress));

      if (showMap || hide) return [];
      return sorted;
    }),
  );

  addressSearchControl = new FormControl<IExpandedAddress | null | string>(null);

  constructor(
    public regionService: RegionOverlayService,
    private store: Store,
    private addressPipe: AddressPipe,
    private addressService: AddressService,
  ) {}

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    this.patchForm(this.store.selectSnapshot(RegionState.addressInput));

    this.subscription.add(
      this.addressSearchControl.valueChanges
        .pipe(
          filter((address) => Boolean(address) && typeof address !== 'string'),
          map((address) => address as IExpandedAddress),
          tap((address) => {
            const shopAddressInput: IShopAddressInput =
              this.addressService.getAddressInputFromExpandedAddress(address);

            this.regionService.addToOperationQ(
              this.store.dispatch(new RegionSetFromAddressInput(shopAddressInput)),
            );
          }),
        )
        .subscribe(),
    );
  }

  userAddressSelected($event: IShopUserAddress) {
    const shopAddressInput: IShopAddressInput =
      this.addressService.getAddressInputFromShopAddress($event);

    this.regionService.addToOperationQ(
      this.store.dispatch(new RegionSetFromAddressInput(shopAddressInput)),
    );

    this.patchForm(shopAddressInput);
    this.hideUserAddresses$.next(true);
  }

  patchForm(address: IShopAddressInput | null) {
    if (!address) return;
    this.addressSearchControl.patchValue(this.addressPipe.transform(address), {
      emitEvent: false,
    });
  }
}
